import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PlatformMetrics from './components/PlatformMetrics';
import AllGymsFinancials from './components/AllGymsFinancials';
import SaasRevenueAnalytics from './components/SaasRevenueAnalytics';
import SystemHealthMonitor from './components/SystemHealthMonitor';
import MultiTenantManagement from './components/MultiTenantManagement';
import GlobalUserManagement from './components/GlobalUserManagement';

const SystemAdministratorDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [financialView, setFinancialView] = useState('all-gyms');
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const platformMetrics = {
    totalActiveGyms: 47,
    totalUsers: 12847,
    subscriptionRevenue: 94500,
    systemUptime: 99.97,
    activeSubscriptions: 45,
    churnRate: 4.3,
    avgRevenuePerGym: 2010,
    supportTickets: 23
  };

  const allGymsFinancialData = {
    totalRevenue: 4847200,
    totalExpenses: 2918320,
    netProfit: 1928880,
    profitMargin: 39.8,
    gymBreakdown: [
      { id: 1, name: 'TrainaryGym Downtown', revenue: 125000, expenses: 75000, profit: 50000, margin: 40, members: 850, status: 'excellent' },
      { id: 2, name: 'TrainaryGym Westside', revenue: 98000, expenses: 61000, profit: 37000, margin: 37.8, members: 720, status: 'good' },
      { id: 3, name: 'TrainaryGym Brooklyn', revenue: 82000, expenses: 54000, profit: 28000, margin: 34.1, members: 650, status: 'warning' },
      { id: 4, name: 'TrainaryGym Queens', revenue: 105000, expenses: 68000, profit: 37000, margin: 35.2, members: 780, status: 'good' },
      { id: 5, name: 'TrainaryGym Bronx', revenue: 89000, expenses: 58000, profit: 31000, margin: 34.8, members: 690, status: 'good' }
    ],
    monthlyTrend: [
      { month: 'Jul', revenue: 4250000, profit: 1700000 },
      { month: 'Aug', revenue: 4380000, profit: 1752000 },
      { month: 'Sep', revenue: 4520000, profit: 1808000 },
      { month: 'Oct', revenue: 4650000, profit: 1860000 },
      { month: 'Nov', revenue: 4720000, profit: 1888000 },
      { month: 'Dec', revenue: 4847200, profit: 1928880 }
    ]
  };

  const saasRevenueData = {
    totalSubscriptionRevenue: 94500,
    activeSubscriptions: 45,
    churnedThisMonth: 2,
    newSubscriptionsThisMonth: 5,
    avgSubscriptionValue: 2100,
    lifetimeValue: 25200,
    subscriptionTiers: [
      { tier: 'Enterprise', count: 8, monthlyRevenue: 39600, price: 4950, features: 'Unlimited users, White-label, API access' },
      { tier: 'Professional', count: 22, monthlyRevenue: 43560, price: 1980, features: 'Up to 1000 members, Advanced analytics' },
      { tier: 'Starter', count: 15, monthlyRevenue: 11340, price: 756, features: 'Up to 300 members, Basic features' }
    ],
    revenueByMonth: [
      { month: 'Jul', revenue: 78000, subscriptions: 39 },
      { month: 'Aug', revenue: 81000, subscriptions: 40 },
      { month: 'Sep', revenue: 84000, subscriptions: 42 },
      { month: 'Oct', revenue: 87000, subscriptions: 43 },
      { month: 'Nov', revenue: 90000, subscriptions: 44 },
      { month: 'Dec', revenue: 94500, subscriptions: 45 }
    ],
    churnAnalysis: {
      churnRate: 4.3,
      topReasons: [
        { reason: 'Cost concerns', percentage: 35 },
        { reason: 'Feature limitations', percentage: 28 },
        { reason: 'Technical issues', percentage: 18 },
        { reason: 'Business closure', percentage: 12 },
        { reason: 'Other', percentage: 7 }
      ]
    }
  };

  const systemHealthData = {
    serverPerformance: {
      cpuUsage: 42,
      memoryUsage: 68,
      diskUsage: 54,
      networkLatency: 23
    },
    databaseMetrics: {
      queryPerformance: 95,
      connectionPool: 78,
      storageUsed: 62,
      backupStatus: 'healthy'
    },
    apiUsage: {
      totalRequests: 2847000,
      avgResponseTime: 145,
      errorRate: 0.12,
      rateLimitHits: 34
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
              System Administrator Dashboard
            </h1>
            <p className="text-muted-foreground">
              Comprehensive platform oversight and SaaS subscription management
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/gym-management-center')}>
              <Icon name="Building2" size={16} className="mr-2" />
              Manage Gyms
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Settings" size={16} className="mr-2" />
              Platform Config
            </Button>
          </div>
        </div>

        <PlatformMetrics metrics={platformMetrics} />

        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              Financial Reconciliation
            </h2>
            <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
              <button
                onClick={() => setFinancialView('all-gyms')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-250 ${
                  financialView === 'all-gyms' ?'bg-primary text-primary-foreground shadow-sm' :'text-foreground hover:bg-background'
                }`}
              >
                <Icon name="Building2" size={16} className="inline mr-2" />
                All Gyms
              </button>
              <button
                onClick={() => setFinancialView('saas-subscriptions')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-250 ${
                  financialView === 'saas-subscriptions' ?'bg-primary text-primary-foreground shadow-sm' :'text-foreground hover:bg-background'
                }`}
              >
                <Icon name="CreditCard" size={16} className="inline mr-2" />
                SaaS Subscriptions
              </button>
            </div>
          </div>

          {financialView === 'all-gyms' ? (
            <AllGymsFinancials data={allGymsFinancialData} selectedPeriod={selectedPeriod} />
          ) : (
            <SaasRevenueAnalytics data={saasRevenueData} selectedPeriod={selectedPeriod} />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SystemHealthMonitor data={systemHealthData} />
          <MultiTenantManagement gyms={allGymsFinancialData?.gymBreakdown} />
        </div>

        <GlobalUserManagement />
      </div>
    </div>
  );
};

export default SystemAdministratorDashboard;